import React, { useEffect } from 'react'

import { GetStaticPropsContext } from 'next'
import styled from 'styled-components'

import { FlexContainer } from '@/components'
import { HeadTag } from '@/components/headTag'
import { HowWeDoContainer, VideoContainer } from '@/containers'
import { CultureHeroContainer } from '@/containers/cultureHero'
import { DayToDayCultureContainer } from '@/containers/dayToDayCulture'
import { OurHistoryContainer } from '@/containers/ourHistory'
import { WorkWithUsContainer } from '@/containers/workWithUs'
import { getCulturePage } from '@/service/api/culturePage'
import {
  CultureHeroData,
  DayToDayCulture,
  HowWeDo,
  OurHistory,
  WorkWithUs,
  CultureVideo,
} from '@/service/api/culturePage/types'
import { Seo } from '@/service/types'
import { Constants } from '@/utils/constants'
import { withFooterHeader } from '@/utils/withFooterHeader'
import { withPageStaticProps } from '@/utils/withPageStaticProps'

export type CultureProps = {
  data: {
    cultureHeroData: CultureHeroData
    ourHistoryData: OurHistory
    howWeDoData: HowWeDo
    videoCultureData: CultureVideo
    dayToDayCultureData: DayToDayCulture
    workWithUsData: WorkWithUs
    seoData: Seo
  }
}

const FlexContainerCulture = styled(FlexContainer)`
  gap: 112px;
  background-color: white;
  max-width: 100%;
  padding: 0;

  @media (max-height: 750px) {
    gap: 96px;
  }
  @media (max-width: 791px) {
    gap: 80px;
  }
`

const Culture: React.FC<CultureProps> = ({
  data: {
    cultureHeroData,
    ourHistoryData,
    howWeDoData,
    videoCultureData,
    dayToDayCultureData,
    workWithUsData,
    seoData,
  },
}) => {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <>
      <HeadTag
        languageOptions={['en']}
        path="/culture"
        title={seoData?.metaTitle}
        description={seoData?.metaDescription}
        image={seoData?.metaImage}
      />
      <FlexContainerCulture direction="column" alignItems="center">
        {cultureHeroData && (
          <CultureHeroContainer cultureHeroData={cultureHeroData} />
        )}
        {ourHistoryData && <OurHistoryContainer data={ourHistoryData} />}
        {howWeDoData && <HowWeDoContainer data={howWeDoData} />}
        {videoCultureData && <VideoContainer src={videoCultureData.src} />}
        {dayToDayCultureData && (
          <DayToDayCultureContainer data={dayToDayCultureData} />
        )}
        {workWithUsData && <WorkWithUsContainer data={workWithUsData} />}
      </FlexContainerCulture>
    </>
  )
}

export const getStaticProps = withPageStaticProps(
  async (context: GetStaticPropsContext) => {
    const { locale } = context
    const data = await getCulturePage(String(locale))

    const cultureHeroData = data.hero
    const ourHistoryData = data.ourHistory
    const howWeDoData = data.howWeDo
    const videoCultureData = data.video
    const dayToDayCultureData = data.dayToDayCulture
    const workWithUsData = data.workWithUs
    const seoData = data.seo

    return {
      props: {
        data: {
          cultureHeroData,
          ourHistoryData,
          howWeDoData,
          videoCultureData,
          dayToDayCultureData,
          workWithUsData,
          seoData,
        },
      },
      revalidate: Constants.ONE_DAY,
    }
  },
)

export default withFooterHeader(Culture)
